import { Alert, Box, Button, FormGroup, Grid, Typography } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { GenericDialog } from 'src/shared/components/organisms/genericDialog';
import FormLoaderWrapper from 'src/shared/components/organisms/formLoaderWrapper';
import ControlledTextField from 'src/shared/components/form/controlledTextField';
import { ControlledCheckboxNew } from 'src/shared/components/form/controlledCheckboxNew';
import ControlledTextEditor from 'src/shared/components/form/controlledTextEditor';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { getUserInviteEmail, inviteUserFromMember } from 'src/shared/services/associationService';

type InviteUserModalProps = {
  open: boolean;
  associationId: string | number | undefined;
  memberId: number;
  onClose: () => void;
  onSaved: () => void;
};

type InviteUserForm = {
  email: string;
  firstName: string;
  lastName: string;
  personalIdentityNumber: string;
  emailLoginEnabled: boolean;
  bankIdLoginEnabled: boolean;
  subject: string;
  body: string;
};

export const InviteUserModal = (props: InviteUserModalProps) => {
  const { open, associationId, memberId, onClose, onSaved } = props;
  const { t } = useTranslation(['user', 'common', 'member']);
  const isMounted = useIsMounted();

  const [loaded, setLoaded] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [alreadyHasAccess, setAlreadyHasAccess] = useState<boolean>(false);

  const formMethods = useForm<InviteUserForm>({
    mode: 'onSubmit',
    defaultValues: {
      email: '',
      firstName: '',
      lastName: '',
      personalIdentityNumber: '',
      emailLoginEnabled: true,
      bankIdLoginEnabled: false,
      subject: '',
      body: '',
    },
  });

  const { handleSubmit, reset, getValues, watch } = formMethods;
  const email = watch('email');

  // Hämta medlems-prefill + default mail, och initiera editorn först när datan finns.
  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await getUserInviteEmail(associationId, memberId);
        if (isMounted()) {
          reset({
            ...getValues(),
            firstName: res.data.firstName ?? '',
            lastName: res.data.lastName ?? '',
            email: res.data.email ?? '',
            personalIdentityNumber: res.data.personalIdentityNumber ?? '',
            subject: res.data.subject ?? '',
            body: res.data.body ?? '',
          });
          setAlreadyHasAccess(res.data.hasUserAccess ?? false);
          setLoaded(true);
        }
      } catch (e) {
        if (isMounted()) {
          ConsoleHelper.log(e);
          setLoaded(true);
        }
      }
    };
    fetchData();
    // Endast vid öppning / byte av medlem
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [associationId, memberId]);

  const onSubmit = useCallback(
    async (data: InviteUserForm) => {
      if (!data.email) {
        toast.error(t('member:inviteUser.missingEmail') as string);
        return;
      }
      if (!data.emailLoginEnabled && !data.bankIdLoginEnabled) {
        toast.error(t('user:createUser.loginMethodRequired') as string);
        return;
      }
      setSaving(true);
      try {
        await inviteUserFromMember(associationId, {
          memberId,
          firstName: data.firstName,
          lastName: data.lastName,
          email: data.email,
          personalIdentityNumber: data.personalIdentityNumber,
          emailLoginEnabled: data.emailLoginEnabled,
          bankIdLoginEnabled: data.bankIdLoginEnabled,
          subject: data.subject,
          body: data.body,
        });
        if (isMounted()) {
          toast.success(t('member:inviteUser.success') as string);
          onSaved();
        }
      } catch (e) {
        if (isMounted()) {
          ConsoleHelper.log(e);
          toast.error(t('member:inviteUser.error') as string);
        }
      } finally {
        if (isMounted()) setSaving(false);
      }
    },
    [associationId, memberId, isMounted, onSaved, t],
  );

  return (
    <GenericDialog
      open={open}
      onClose={onClose}
      onSave={() => handleSubmit(onSubmit)()}
      dialogTitle={t('member:inviteUser.title')}
      maxWidth='md'
      dialogContent={
        <FormLoaderWrapper loading={!loaded || saving}>
          <FormProvider {...formMethods}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <Box sx={{ p: 1 }}>
                <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
                  {t('member:inviteUser.description')}
                </Typography>
                {alreadyHasAccess && (
                  <Alert severity='info' sx={{ mb: 2 }}>
                    {t('member:inviteUser.alreadyHasAccess')}
                  </Alert>
                )}
                {loaded && !email && (
                  <Alert severity='warning' sx={{ mb: 2 }}>
                    {t('member:inviteUser.missingEmail')}
                  </Alert>
                )}
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <ControlledTextField required name='firstName' label={t('user:createUser.firstName')} fullWidth size='small' />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <ControlledTextField required name='lastName' label={t('user:createUser.lastName')} fullWidth size='small' />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <ControlledTextField required name='email' label={t('user:createUser.email')} fullWidth size='small' />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <ControlledTextField name='personalIdentityNumber' label={t('user:createUser.personalIdentityNumber')} fullWidth size='small' />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant='body2' sx={{ mb: 0.5 }}>
                      {t('user:createUser.loginMethods')}
                    </Typography>
                    <FormGroup row>
                      <ControlledCheckboxNew name='emailLoginEnabled' label={t('user:createUser.emailLoginEnabled')} />
                      <ControlledCheckboxNew name='bankIdLoginEnabled' label={t('user:createUser.bankIdLoginEnabled')} />
                    </FormGroup>
                  </Grid>
                  <Grid item xs={12}>
                    <ControlledTextField required name='subject' label={t('member:inviteUser.subject')} fullWidth size='small' />
                  </Grid>
                  {loaded && (
                    <Grid item xs={12}>
                      <ControlledTextEditor name='body' rows={22} noMargin />
                    </Grid>
                  )}
                </Grid>
              </Box>
            </form>
          </FormProvider>
        </FormLoaderWrapper>
      }
      dialogActions={
        <Box display='flex' flexDirection='row' justifyContent='flex-end' width='100%'>
          <Button variant='outlined' sx={{ mr: 1 }} onClick={onClose} disabled={saving}>
            {t('common:button.cancel')}
          </Button>
          <Button variant='contained' disableElevation onClick={() => handleSubmit(onSubmit)()} disabled={saving || !loaded || !email}>
            {t('member:inviteUser.sendButton')}
          </Button>
        </Box>
      }
    />
  );
};
